/**
 * @fileoverview Mock-only in-memory Activity store.
 * @description Seeds from activities-seed.json and holds it in a module-level array, same
 * in-memory-only pattern as tenants-mock-store.ts. Entries are append-only — "Saved activities
 * cannot be edited. Corrections or updates must be added as new entries" — so this store
 * deliberately exposes no update/delete function. Never imported outside this feature's mocks/
 * folder.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import seedData from './activities-seed.json'

import type { Activity } from '@/features/sales/types/tenant.types'

const activities: Activity[] = seedData.map((activity) => ({
  ...activity,
  type: activity.type as Activity['type'],
}))

let nextSequence = activities.length + 1

/**
 * @description Lists every activity recorded against one tenant, oldest first.
 * @param {string} tenantId - The tenant whose activity history to return.
 * @returns {Activity[]} The tenant's activities, in recorded order.
 */
export function listActivitiesForTenant(tenantId: string): Activity[] {
  return activities
    .filter((activity) => activity.tenantId === tenantId)
    .sort((a, b) => a.recordedAt.localeCompare(b.recordedAt))
}

export interface AddActivityInput {
  type: Activity['type']
  details: string
  scheduledAt?: string | null
  attachments?: string[]
  authorId: string
}

/**
 * @description Appends a new activity entry against a tenant. There is no corresponding update
 * function by design — see the file header.
 * @param {string} tenantId - The tenant the activity is recorded against.
 * @param {AddActivityInput} input - The activity's details and author.
 * @returns {Activity} The created activity.
 */
export function addActivity(tenantId: string, input: AddActivityInput): Activity {
  const activity: Activity = {
    id: `ACT-${String(4000 + nextSequence)}`,
    tenantId,
    type: input.type,
    details: input.details,
    scheduledAt: input.scheduledAt ?? null,
    attachments: input.attachments ?? [],
    authorId: input.authorId,
    recordedAt: new Date().toISOString(),
  }
  nextSequence += 1
  activities.push(activity)
  return activity
}
